import React, { useState, useEffect } from 'react';
import axios from 'axios';

const UserManager = () => {
  const [users, setUsers] = useState([]);
  const [conflicts, setConflicts] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [ipHistory, setIpHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('users'); // 'users' or 'conflicts'
  const [searchTerm, setSearchTerm] = useState('');
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0
  });

  useEffect(() => {
    if (activeTab === 'users') {
      fetchUsers();
    } else {
      fetchIPConflicts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, pagination.page]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`/api/admin/users?page=${pagination.page}&limit=${pagination.limit}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data.users || []);
      setPagination(response.data.pagination || pagination);
    } catch (error) {
      console.error('Error fetching users:', error);
      alert('Kullanıcılar yüklenirken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const fetchIPConflicts = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('adminToken');
      const response = await axios.get('/api/admin/ip-conflicts', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConflicts(response.data.conflicts || []);
    } catch (error) {
      console.error('Error fetching IP conflicts:', error);
      alert('IP çakışmaları yüklenirken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const fetchUserIPHistory = async (userId) => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`/api/admin/users/${userId}/ip-history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setIpHistory(response.data.history || []);
      setSelectedUser(userId);
    } catch (error) {
      console.error('Error fetching IP history:', error);
      alert('IP geçmişi yüklenirken bir hata oluştu');
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    if (!window.confirm(`Kullanıcının rolünü "${newRole === 'admin' ? 'Admin' : newRole === 'moderator' ? 'Moderatör' : 'Kullanıcı'}" olarak değiştirmek istediğinize emin misiniz?`)) {
      return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem('adminToken');
      await axios.put(`/api/admin/users/${userId}/role`, { role: newRole }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      alert('Kullanıcı rolü başarıyla güncellendi');
      await fetchUsers();
    } catch (error) {
      console.error('Error updating user role:', error);
      alert(error.response?.data?.error || 'Rol güncellenirken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async (userId, username) => {
    if (!window.confirm(`"${username}" kullanıcısını silmek istediğinize emin misiniz? Bu işlem geri alınamaz!`)) {
      return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem('adminToken');
      const response = await axios.delete(`/api/admin/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.message) {
        alert(response.data.message);
      } else {
        alert('Kullanıcı başarıyla silindi');
      }
      
      // Refresh user list
      await fetchUsers();
    } catch (error) {
      console.error('Error deleting user:', error);
      const errorMessage = error.response?.data?.error || error.response?.data?.details || error.message || 'Kullanıcı silinirken bir hata oluştu';
      alert(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const getConflictSeverity = (userCount) => {
    if (userCount >= 5) return 'high';
    if (userCount >= 3) return 'medium';
    return 'low';
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'high':
        return 'bg-red-500/20 border-red-500 text-red-400';
      case 'medium':
        return 'bg-yellow-500/20 border-yellow-500 text-yellow-400';
      case 'low':
        return 'bg-blue-500/20 border-blue-500 text-blue-400';
      default:
        return 'bg-gray-500/20 border-gray-500 text-gray-400';
    }
  };

  const filteredUsers = searchTerm
    ? users.filter(user =>
        user.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.telegramUsername?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.ipAddress?.includes(searchTerm)
      )
    : users;

  return (
    <div className="bg-gradient-card rounded-xl p-6 border border-gold/30">
      <h2 className="text-2xl font-bold text-gold mb-6">Üye Yönetimi</h2>

      {/* Tabs */}
      <div className="mb-6 flex gap-2 border-b border-gold/20">
        <button
          onClick={() => {
            setActiveTab('users');
            setSearchTerm('');
          }}
          className={`px-6 py-3 font-semibold transition-colors ${
            activeTab === 'users'
              ? 'text-gold border-b-2 border-gold'
              : 'text-white/60 hover:text-gold'
          }`}
        >
          Üye Listesi
        </button>
        <button
          onClick={() => setActiveTab('conflicts')}
          className={`px-6 py-3 font-semibold transition-colors ${
            activeTab === 'conflicts'
              ? 'text-gold border-b-2 border-gold'
              : 'text-white/60 hover:text-gold'
          }`}
        >
          IP Çakışmaları
        </button>
      </div>

      {/* Users Tab */}
      {activeTab === 'users' && (
        <div>
          {/* Search */}
          <div className="mb-4">
            <input
              type="text"
              placeholder="Kullanıcı adı, e-posta, Telegram veya IP ile ara..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-4 py-3 bg-navy-700 border border-gold/30 rounded-lg text-white focus:outline-none focus:border-gold"
            />
          </div>

          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-navy-800/50 p-4 rounded-lg border border-gold/20">
              <div className="text-white/60 text-sm">Toplam Üye</div>
              <div className="text-gold font-bold text-2xl">{pagination.total}</div>
            </div>
            <div className="bg-navy-800/50 p-4 rounded-lg border border-gold/20">
              <div className="text-white/60 text-sm">Bu Sayfa</div>
              <div className="text-gold font-bold text-2xl">{filteredUsers.length}</div>
            </div>
            <div className="bg-navy-800/50 p-4 rounded-lg border border-gold/20">
              <div className="text-white/60 text-sm">Sayfa</div>
              <div className="text-gold font-bold text-2xl">{pagination.page} / {pagination.pages || 1}</div>
            </div>
            <div className="bg-navy-800/50 p-4 rounded-lg border border-gold/20">
              <div className="text-white/60 text-sm">IP Çakışması</div>
              <div className="text-gold font-bold text-2xl">{conflicts.length}</div>
            </div>
          </div>

          {loading ? (
            <div className="text-center text-white py-8">Yükleniyor...</div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <thead>
                    <tr className="border-b border-gold/30">
                      <th className="px-4 py-3 text-gold font-semibold">ID</th>
                      <th className="px-4 py-3 text-gold font-semibold">Kullanıcı Adı</th>
                      <th className="px-4 py-3 text-gold font-semibold">Telegram</th>
                      <th className="px-4 py-3 text-gold font-semibold">E-posta</th>
                      <th className="px-4 py-3 text-gold font-semibold">Rol</th>
                      <th className="px-4 py-3 text-gold font-semibold">Puan</th>
                      <th className="px-4 py-3 text-gold font-semibold">IP Adresi</th>
                      <th className="px-4 py-3 text-gold font-semibold">Kayıt Tarihi</th>
                      <th className="px-4 py-3 text-gold font-semibold">İşlemler</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.length === 0 ? (
                      <tr>
                        <td colSpan="9" className="px-4 py-8 text-center text-white/60">
                          {searchTerm ? 'Arama sonucu bulunamadı' : 'Kullanıcı bulunamadı'}
                        </td>
                      </tr>
                    ) : (
                      filteredUsers.map((user) => {
                        const getRoleDisplay = (role) => {
                          switch (role) {
                            case 'admin':
                              return { text: 'Admin', color: 'text-red-400', bg: 'bg-red-500/20', border: 'border-red-500' };
                            case 'moderator':
                              return { text: 'Moderatör', color: 'text-yellow-400', bg: 'bg-yellow-500/20', border: 'border-yellow-500' };
                            default:
                              return { text: 'Kullanıcı', color: 'text-blue-400', bg: 'bg-blue-500/20', border: 'border-blue-500' };
                          }
                        };
                        const roleDisplay = getRoleDisplay(user.role || 'user');
                        return (
                          <tr key={user.id} className="border-b border-gold/10 hover:bg-navy-800/50">
                            <td className="px-4 py-3 text-white">{user.id}</td>
                            <td className="px-4 py-3 text-white font-semibold">{user.username}</td>
                            <td className="px-4 py-3 text-white/80">@{user.telegramUsername}</td>
                            <td className="px-4 py-3 text-white/80">{user.email}</td>
                            <td className="px-4 py-3">
                              <select
                                value={user.role || 'user'}
                                onChange={(e) => handleRoleChange(user.id, e.target.value)}
                                className={`px-3 py-1 rounded-lg border ${roleDisplay.bg} ${roleDisplay.border} ${roleDisplay.color} font-semibold text-sm cursor-pointer focus:outline-none`}
                              >
                                <option value="user">Kullanıcı</option>
                                <option value="moderator">Moderatör</option>
                                <option value="admin">Admin</option>
                              </select>
                            </td>
                            <td className="px-4 py-3 text-gold font-semibold">💰 {user.coins || 0}</td>
                            <td className="px-4 py-3 text-white font-mono text-sm">
                              {user.ipAddress || '-'}
                            </td>
                            <td className="px-4 py-3 text-white/80 text-sm">
                              {new Date(user.createdAt).toLocaleDateString('tr-TR', {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex gap-2">
                                <button
                                  onClick={() => fetchUserIPHistory(user.id)}
                                  className="bg-gradient-to-r from-gold to-yellow-500 hover:from-yellow-500 hover:to-gold text-navy-900 font-bold px-3 py-1 rounded-lg transition-all duration-300 text-sm"
                                >
                                  IP Geçmişi
                                </button>
                                <button
                                  onClick={() => handleDeleteUser(user.id, user.username)}
                                  className="bg-red-500 hover:bg-red-600 text-white font-bold px-3 py-1 rounded-lg transition-all duration-300 text-sm"
                                >
                                  Sil
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {!searchTerm && pagination.pages > 1 && (
                <div className="flex justify-center gap-2 mt-6">
                  <button
                    onClick={() => setPagination({ ...pagination, page: pagination.page - 1 })}
                    disabled={pagination.page === 1}
                    className="px-4 py-2 bg-navy-700 hover:bg-navy-600 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
                  >
                    Önceki
                  </button>
                  <span className="px-4 py-2 text-white">
                    Sayfa {pagination.page} / {pagination.pages}
                  </span>
                  <button
                    onClick={() => setPagination({ ...pagination, page: pagination.page + 1 })}
                    disabled={pagination.page >= pagination.pages}
                    className="px-4 py-2 bg-navy-700 hover:bg-navy-600 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
                  >
                    Sonraki
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* IP Conflicts Tab */}
      {activeTab === 'conflicts' && (
        <div>
          {loading ? (
            <div className="text-center text-white py-8">Yükleniyor...</div>
          ) : conflicts.length === 0 ? (
            <div className="text-center text-white py-8 bg-navy-800/50 rounded-lg">
              IP çakışması bulunamadı. Tüm kullanıcılar farklı IP adreslerine sahip.
            </div>
          ) : (
            <div className="space-y-4">
              {conflicts.map((conflict, index) => {
                const severity = getConflictSeverity(conflict.userCount);
                return (
                  <div
                    key={index}
                    className={`p-4 rounded-lg border-2 ${getSeverityColor(severity)}`}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <span className="font-bold text-lg">IP: {conflict.ipAddress}</span>
                        <span className="ml-3 px-3 py-1 rounded-full bg-white/10 text-sm">
                          {conflict.userCount} Kullanıcı
                        </span>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                        severity === 'high' ? 'bg-red-500 text-white' :
                        severity === 'medium' ? 'bg-yellow-500 text-navy-900' :
                        'bg-blue-500 text-white'
                      }`}>
                        {severity === 'high' ? 'Yüksek Risk' :
                         severity === 'medium' ? 'Orta Risk' : 'Düşük Risk'}
                      </span>
                    </div>
                    <div className="space-y-2">
                      {conflict.users.map((user) => (
                        <div
                          key={user.id}
                          className="bg-black/20 p-3 rounded-lg flex items-center justify-between"
                        >
                          <div>
                            <span className="font-semibold text-white">{user.username}</span>
                            <span className="text-white/60 ml-3">({user.email})</span>
                            <span className="text-white/60 ml-3">
                              @{user.telegramUsername}
                            </span>
                            <span className="text-gold ml-3 font-semibold">
                              💰 {user.coins || 0}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-white/60 text-sm">
                              {new Date(user.createdAt).toLocaleDateString('tr-TR')}
                            </span>
                            <button
                              onClick={() => fetchUserIPHistory(user.id)}
                              className="px-3 py-1 bg-gold/20 hover:bg-gold/30 text-gold rounded text-sm transition-colors"
                            >
                              IP Geçmişi
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* IP History Modal */}
      {selectedUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-gradient-card rounded-xl p-6 border border-gold/30 max-w-2xl w-full max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-gold">IP Geçmişi</h3>
              <button
                onClick={() => {
                  setSelectedUser(null);
                  setIpHistory([]);
                }}
                className="text-white hover:text-gold text-2xl"
              >
                ×
              </button>
            </div>
            {ipHistory.length === 0 ? (
              <div className="text-center text-white py-8">IP geçmişi bulunamadı</div>
            ) : (
              <div className="space-y-2">
                {ipHistory.map((entry, index) => (
                  <div
                    key={index}
                    className="bg-navy-800 p-4 rounded-lg border border-gold/20"
                  >
                    <div className="flex justify-between items-center">
                      <div>
                        <span className="font-mono text-gold font-semibold">{entry.ipAddress || entry.ip_address}</span>
                        <span className="ml-3 px-2 py-1 bg-gold/20 text-gold rounded text-sm">
                          {entry.action === 'register' ? 'Kayıt' : 'Giriş'}
                        </span>
                      </div>
                      <span className="text-white/60 text-sm">
                        {new Date(entry.createdAt || entry.created_at).toLocaleString('tr-TR')}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManager;
